<script setup lang="ts">
import { ref } from "vue";

import type { variant, color, type } from "@/Modules/App/Models/Styles/Button";
import type { iconName, iconSize } from "@/Modules/App/Models/Styles/Icons";
import type { colorTag } from "@/Modules/App/Models/Styles/Colors";

import TcLoader from "./TcLoader.vue";
import IconResolver from "@/Components/Extend/TcIcon.vue";

import { createRipple } from "@/Assets/javascript/Ripple";
import { isValidString } from "@/Utils/String";

export interface TcButtonProps {
  type?: type;
  variant?: variant;
  isSmall?: boolean;
  isLarge?: boolean;
  isFitContent?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  color?: color;
  colorLoading?: colorTag;
  prefixIcon?: iconName;
  prefixIconSize?: iconSize;
  prefixIconColor?: colorTag;
  sufixIcon?: iconName;
  sufixIconSize?: iconSize;
  sufixIconColor?: colorTag;
  isRounded?: boolean;
  isBlock?: boolean;
}

const emit = defineEmits<{
  (event: "click", value: Event): void;
}>();

const btnRef = ref<HTMLHtmlElement>();

const props = withDefaults(defineProps<TcButtonProps>(), {
  variant: "contained",
  color: "content_high",
  type: "button",
  isLoading: false,
  colorLoading: "content-inverse",
  prefixIconColor: "content-inverse",
  sufixIconColor: "content-inverse",
  isRounded: true,
  isBlock: true,
});

const onClick = (e: MouseEvent) => {
  if (!props.disabled && !props.isLoading) {
    if (props.variant === "contained" && btnRef.value) {
      createRipple(e, btnRef.value);
    }

    if (props.type !== "submit") {
      emit("click", e);
    }
  }
};
</script>

<template>
  <button
    :type="props.type"
    ref="btnRef"
    class="btn"
    :class="{
      [`btn--${props.variant}`]: true,
      'btn--small': props.isSmall,
      'btn--fit_content': props.isFitContent,
      'btn--large': props.isLarge,
      'btn--loading': props.isLoading,
      'btn--rounded': props.isRounded,
      'btn--block': props.isBlock,
      'btn__prefix-icon': !!props.prefixIcon,
      'btn__sufix-icon': !!props.sufixIcon,
      [`btn--${props.variant}--${props.color}`]: true,
    }"
    :disabled="disabled"
    @click="onClick"
  >
    <span>
      <IconResolver
        v-if="isValidString(props.prefixIcon)"
        :name="props.prefixIcon"
        :color="props.prefixIconColor"
        :size="props.prefixIconSize"
        class="btn__prefix-icon--margin"
      />

      <slot />

      <IconResolver
        v-if="isValidString(props.sufixIcon)"
        :name="props.sufixIcon"
        :color="props.sufixIconColor"
        :size="props.sufixIconSize"
        class="btn__sufix-icon--margin"
      />
    </span>

    <TcLoader v-if="props.isLoading" class="btn__loader" :color="colorLoading" :size="24" />
  </button>
</template>

<style lang="scss"></style>
